import { Files } from "@bundle/core";
import type { FileRef } from "@bundle/contracts";
import type { IconName } from "@bundle/tokens";
import { useState } from "react";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";
import { Button, Icon, IconButton, Image, MediaViewer, Menu, Pressable, Stack, Text, Video, type MediaViewerItem, type MenuItem } from "../primitives/index.ts";
import { useSharingApi } from "../context/SharingContext.tsx";
import { FileShareSheet } from "./sharing/FileShareSheet.tsx";

/** What the files of a message can do for the viewer (from `useFileActions`). */
export type FileActions = {
  /** `can.downloadFiles` (guests/agents may not): originals, videos and documents need it. */
  canDownload: boolean;
  onDownload: (file: FileRef) => void;
  onCopyLink: (file: FileRef) => void;
  /** Opens the file's page (details, who can access it). */
  onDetails: (file: FileRef) => void;
  /** "Save as sticker" on images (absent: stickers off, or I can't create them). */
  onSaveAsSticker?: (file: FileRef) => void;
  /** "Share…": the file's Share dialog (absent: sharing off). */
  onShare?: (file: FileRef) => void;
};

export type MessageFilesProps = {
  files: FileRef[];
  actions: FileActions;
  /** Optimistic message (local previews): no menus, no viewer. */
  pending?: boolean;
  testID: string;
};

const GRID_WIDTH = 400;

function menuFor(t: ReturnType<typeof useT>, f: FileRef, a: FileActions): MenuItem[] {
  const items: MenuItem[] = [];
  if (a.canDownload) items.push({ id: "download", label: t("files.download"), icon: "download", onSelect: () => a.onDownload(f) });
  const share = a.onShare;
  if (share !== undefined && a.canDownload) items.push({ id: "share", label: t("files.share"), icon: "users", onSelect: () => share(f) });
  items.push({ id: "copy-link", label: t("files.copyLink"), icon: "link", onSelect: () => a.onCopyLink(f) });
  items.push({ id: "details", label: t("files.details"), icon: "externalLink", onSelect: () => a.onDetails(f) });
  const save = a.onSaveAsSticker;
  if (save !== undefined && Files.fileKind(f.mime, f.name) === "image") items.push({ id: "save-sticker", label: t("files.saveAsSticker"), icon: "sticker", onSelect: () => save(f) });
  return items;
}

/** One document / generic file: icon, name, kind and size, download and a menu. */
export function FileCard({ file, actions, pending, testID }: { file: FileRef; actions: FileActions; pending?: boolean; testID: string }) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const kind = Files.fileKind(file.mime, file.name);
  const meta = `${tr(Files.kindLabel(kind))} · ${Files.formatFileSize(file.size, locale)}`;
  return (
    <Stack direction="horizontal" align="center" gap="3" padding="2.5" radius="lg" border="border.subtle" background="bg.surface" testID={testID}>
      <Pressable direction="horizontal" radius="md" onPress={() => (pending === true ? undefined : actions.onDetails(file))} accessibilityLabel={t("files.open", { name: file.name })} testID={`${testID}-open`}>
        <Stack direction="horizontal" align="center" gap="3">
          <Stack padding="2" radius="md" background="accent.subtle">
            <Icon name={Files.fileIcon(kind) as IconName} size="icon.lg" color="accent.text" />
          </Stack>
          <Stack gap="0.5">
            <Text weight="semibold" lines={1}>
              {file.name}
            </Text>
            <Text variant="caption" color="text.secondary" lines={1}>
              {meta}
            </Text>
          </Stack>
        </Stack>
      </Pressable>
      {pending === true ? null : (
        <Stack direction="horizontal" align="center" gap="0.5">
          <IconButton
            icon="download"
            size="sm"
            accessibilityLabel={actions.canDownload ? t("files.download") : t("files.reason.guestNoDownload")}
            disabled={!actions.canDownload}
            onPress={() => actions.onDownload(file)}
            testID={`${testID}-download`}
          />
          <Menu items={menuFor(t, file, actions)} align="end" testID={`${testID}-menu`}>
            <IconButton icon="more" size="sm" accessibilityLabel={t("message.more")} onPress={() => {}} testID={`${testID}-more`} />
          </Menu>
        </Stack>
      )}
    </Stack>
  );
}

/**
 * The files of one message: images in a grid (1 → its own shape, 2 side by side, 3 = one wide + two, 4+ = 2×2 with
 * "+N"), videos as inline players, everything else as cards. Images and videos open in the media viewer.
 */
export function MessageFiles({ files, actions: base, pending, testID }: MessageFilesProps) {
  const t = useT();
  const [viewing, setViewing] = useState<number | undefined>(undefined);
  // Sharing on (SharingContext): "Share…" in the file menus opens the file's Share dialog here.
  const sharingOn = useSharingApi() !== null;
  const [sharing, setSharing] = useState<string | undefined>(undefined);
  const actions: FileActions = sharingOn && base.onShare === undefined ? { ...base, onShare: (f) => setSharing(f.id) } : base;
  const images = files.filter((f) => Files.fileDisplay(f, actions.canDownload) === "image");
  const videos = files.filter((f) => Files.fileDisplay(f, actions.canDownload) === "video");
  const cards = files.filter((f) => Files.fileDisplay(f, actions.canDownload) === "card");
  const viewable = [...images, ...videos];
  const items: MediaViewerItem[] = viewable.map((f) => {
    const item: MediaViewerItem = { src: Files.imageSource(f), kind: images.includes(f) ? "image" : "video", title: f.name };
    if (f.width !== undefined) item.width = f.width;
    if (f.height !== undefined) item.height = f.height;
    if (f.thumbUrl !== undefined) item.poster = f.thumbUrl;
    if (item.kind === "video") {
      item.src = f.url;
      item.mime = f.mime;
    }
    return item;
  });
  const grid = Files.imageGrid(images.map((f) => ({ width: f.width, height: f.height })), GRID_WIDTH);
  const current = viewing === undefined ? undefined : viewable[viewing];
  const open = (i: number) => {
    if (pending !== true) setViewing(i);
  };
  return (
    <Stack gap="2" align="start" testID={testID}>
      {images.length === 0 ? null : (
        <Stack gap="1" align="start" testID={`${testID}-grid`}>
          {grid.rows.map((row) => (
            <Stack key={row.cells[0]!.index} direction="horizontal" gap="1" align="start">
              {row.cells.map((cell) => {
                const f = images[cell.index]!;
                return (
                  <Pressable
                    key={f.id}
                    radius="md"
                    onPress={() => open(cell.index)}
                    contextMenu={pending === true ? undefined : menuFor(t, f, actions)}
                    accessibilityLabel={t("files.open", { name: f.name })}
                    testID={`${testID}-image-${cell.index}`}
                  >
                    <Image src={grid.rows.length === 1 && row.cells.length === 1 ? Files.imageSource(f) : f.thumbUrl ?? Files.imageSource(f)} alt={f.name} width={cell.width} height={cell.height} radius="md" />
                  </Pressable>
                );
              })}
            </Stack>
          ))}
          {grid.rows.flatMap((r) => r.cells).some((c) => c.more > 0) ? (
            <Button
              label={t("files.more", { count: images.length - 4 })}
              variant="ghost"
              size="sm"
              onPress={() => open(4)}
              testID={`${testID}-more`}
            />
          ) : null}
        </Stack>
      )}
      {videos.map((f) => {
        const size = Files.imageGrid([{ width: f.width, height: f.height }], GRID_WIDTH);
        return (
          <Stack key={f.id} gap="1" align="start" testID={`${testID}-video-${f.id}`}>
            <Video src={f.url} poster={f.thumbUrl} title={f.name} width={size.width} height={size.height} radius="md" testID={`${testID}-player-${f.id}`} />
            <Stack direction="horizontal" align="center" gap="1">
              <Text variant="caption" color="text.secondary" lines={1}>
                {f.name}
              </Text>
              {pending === true ? null : (
                <Menu items={menuFor(t, f, actions)} align="start" testID={`${testID}-video-menu-${f.id}`}>
                  <IconButton icon="more" size="sm" accessibilityLabel={t("message.more")} onPress={() => {}} testID={`${testID}-video-more-${f.id}`} />
                </Menu>
              )}
            </Stack>
          </Stack>
        );
      })}
      {cards.map((f) => (
        <FileCard key={f.id} file={f} actions={actions} pending={pending} testID={`${testID}-file-${f.id}`} />
      ))}
      <MediaViewer
        open={current !== undefined}
        index={viewing ?? 0}
        items={items}
        onClose={() => setViewing(undefined)}
        onIndexChange={(i) => setViewing(i)}
        actions={
          current === undefined
            ? []
            : menuFor(t, current, actions).map((a) =>
                a.id === "details" || a.id === "share"
                  ? {
                      ...a,
                      onSelect: () => {
                        setViewing(undefined);
                        a.onSelect();
                      },
                    }
                  : a,
              )
        }
        labels={{
          close: t("common.close"),
          previous: t("files.viewer.prev"),
          next: t("files.viewer.next"),
          counter: t("files.viewer.counter", { index: (viewing ?? 0) + 1, total: viewable.length }),
        }}
        testID={`${testID}-viewer`}
      />
      {sharing === undefined ? null : <FileShareSheet fileId={sharing} onClose={() => setSharing(undefined)} />}
    </Stack>
  );
}
